import { ComponentType, InputType, ProgramStatus, ProgramTypeKey, ProgramTypes } from '@k12/models';
import { UserType } from '../../../app-services/src/households/household.service';

export interface Prompt {
  comparisonOperator?: string;
  components: PromptComponent[];
  /** GUID of the governing input on the parent decision component (decision child prompts only). */
  conditionInputId?: string;
  conditionValue?: string;
  hasCondition?: boolean;
  header?: string;
  id?: string;
  instructions?: string;
  isRequired: boolean;
  name?: string;
  note?: string;
  paragraph?: string;
  parentPromptToken?: string;
  parentPromptTokens?: string[];
  title?: string;
  token: string;
}

export interface PromptComponent {
  buttonLabel?: string;
  componentType?: ComponentType;
  fields?: PromptField[];
  header?: string;
  id?: string;
  inputs: PromptInput[];
  inputType?: string;
  instructions?: string;
  label: string;
  max?: number;
  order?: number;
  paragraph?: string;
  sections?: PromptSection[];
  subHeader?: string;
  token?: string;
}

export interface PromptInput {
  conditionValue?: string;
  dataType: string;
  fileSize?: number;
  fileType?: [] | string[];
  icon?: string;
  /** Server-assigned GUID identity. Undefined for newly authored inputs not yet saved. */
  id?: string;
  inputType?: string;
  instructions?: string;
  isRequired?: boolean;
  label?: string;
  link?: PromptInputLink;
  /**
   * Client-only stable identity for inputs that don't yet have a server-assigned `id`
   * (e.g. an unsaved row added in the admin builder). Assigned once, when the input is
   * created, so templates can use it as a `@for` track key without invoking a method on
   * every change-detection pass. Never sent to the backend -- strip it before persisting.
   */
  localTrackId?: string;
  max?: number;
  min?: number;
  newPromptName?: string;
  options?: PromptInputOption[];
  order?: number;
  placeholder?: string;
  selected?: boolean;
  subLabel?: string;
  validatorList?: [];
  value?: string;
}

/**
 * Reserved label of the server-owned input that carries which path an applicant chose on a
 * select-sub-select component. The visible checkbox inputs hold sub-selections keyed by their own
 * id, so the chosen path itself needs its own answer for branch conditions to evaluate. Authored
 * and maintained entirely by the API; never rendered, never emitted by the admin builder.
 */
export const SUB_SELECT_DISCRIMINATOR_LABEL = '__selected_path';

export const isDiscriminatorInput = (input: PromptInput): boolean =>
  input.inputType === InputType.SingleSelection && input.label === SUB_SELECT_DISCRIMINATOR_LABEL;

export const findDiscriminatorInput = (component?: PromptComponent): PromptInput | undefined =>
  component?.inputs?.find(isDiscriminatorInput);

/** The author-managed path inputs of a select-sub-select component (one per branch). */
export const subSelectPathInputs = (component?: PromptComponent): PromptInput[] =>
  (component?.inputs ?? []).filter((input) => !isDiscriminatorInput(input));

export interface PromptField {
  style: string;
  type: string;
  value: string;
}

export interface PromptSection {
  title: string;
  subsection: PromptSubSection[];
}

export interface PromptSubSection {
  category: string;
  fields: PromptSectionField[];
  title: string;
}

export interface PromptSectionField {
  field: string;
  label: string;
  options: string[];
  required: boolean;
  type: string;
  value: string;
}

export interface PromptInputLink {
  terms?: string,
  privacy?: string
}

export interface PromptInputOption {
  columns?: Record<string, string | string[] | boolean>[];
  conditionValue?: string;
  dataType?: string;
  label: string;
  // Name for a downstream prompt created via the "+ Create new prompt" branch target. Kept
  // separate from `label` (the option button text) so the two are not conflated.
  newPromptName?: string;
  order: number;
  required?: boolean;
  selected?: boolean;
  subLabel?: string;
  value?: string;
}

export interface SubmitPrompt {
  enrollmentSessionId: string;
  inputValues: SubmitInputValue[];
  promptToken: string;
}

export interface SubmitInputValue {
  inputId: string;
  value: string;
}

/**
 * One file attached to a file_upload input. `attachmentId` is what the wizard submits as the
 * input's answer, so the stored value resolves to a blob rather than to a name the server cannot
 * look up.
 */
export interface PromptFileAttachment {
  attachmentId: string;
  fileName: string;
  fileExtension: string;
}

export interface SubmitPromptResponse {
  enrollmentProgramToken: string;
  locationUrl: string;
  nextPrompt: NextPrompt;
  prompt: NextPrompt;
  promptDataDictionary?: PromptDataDictionary;
  promptId: string;
  sessionId: string;
}

export interface NextPrompt extends Prompt {
  promptDataDictionary: PromptDataDictionary
}

/** Prefill values keyed by input id (plain GUID string). */
export interface PromptDataDictionary {
  [inputId: string]: {
    applicationId: string;
    id: string;
    inputId: string;
    value: string;
  }
}

export interface PromptData {
  id: string;
  applicationId: string;
  inputId: string;
  value: string;
}

export interface Program {
  createdBy: string;
  lastModified: string;
  name: string;
  programType: ProgramTypeKey;
  prompts: Prompt[];
  publishedDate: string;
  status: ProgramStatus;
  token: string;
  type: ProgramTypes;
  userTypeId: number;
  userType: string;
  version: number;
}

export interface SaveProgramResponse {
  token: string;
  name: string;
  version: number;
  prompts?: Prompt[];
  isCurrent: boolean;
  isPublished: boolean;
  requiresPublish: boolean;
  userTypeId: UserType;
  userType: null;
  programType: ProgramTypeKey;
  status: string;
}

export interface PromptCondition {
  parentPromptToken: string;
  conditionValue: string;
}

export interface ProgramTreePrompt {
  /** Every branch value this prompt's decision can take, in author order. Empty for non-decisions. */
  branchValues?: string[];
  conditions: PromptCondition[];
  conditionValue?: string;
  hasDecisionComponent: boolean;
  hasDecisionChildren: number;
  hasReviewComponent: boolean;
  isDecisionParent: boolean;
  name: string;
  parentPromptTokens: undefined | string[];
  promptToken: string;
}

export interface DagreTreeData {
  edge: DagreEdge[];
  node: DagreNode[];
}

export interface DagreEdge {
  isAdd: boolean;
  /**
   * The branch value this edge is conditioned on, or undefined for a plain (linear) edge. Any
   * number of distinct values may fan out of one source; 'true'/'false' are the binary decision's
   * spelling and get the check/cross glyph, everything else renders as a text label.
   */
  branchValue?: string;
  source: DagreNode | string;
  target: DagreNode | string;
}

export interface DagreNode {
  id: string;
  label: string;
  parentId: string;
  data: {
    /** For injected add-buttons under a decision prompt: which branch value this button creates. */
    branchCondition?: string;
    /** Every branch value this prompt's decision can take. Empty for non-decisions. */
    branchValues?: string[];
    hasChild: boolean;
    hasDecisionChildren: number;
    hasReviewComponent: boolean;
    isAdd: boolean;
    isDecisionParent: boolean;
    parentPromptTokens: string[];
  };
}

export interface SubSelectRemoveButton {
  input: number | undefined;
  option: number | undefined;
}

export interface ProgramReview {
  enrollmentProgramToken: string;
  prompts: PromptReview[]
  sessionId: string;
}

export interface PromptReview {
  components: ComponentsReview[];
  header: string | null;
  token: string;
  title: string;
}

export interface ComponentsReview {
  header: string;
  inputs: InputsReview[];
  order: number;
}

export interface InputsReview {
  label: string;
  options: string[];
  order: number;
  value: string;
}

export interface EnrollmentPeriod {
  id?: string;
  academicYearId?: number;
  programType?: ProgramTypeKey;
  personaId?: number;
  program: {
    label: string;
    value: ProgramTypeKey;
  };
  // Persona (UserType) this program type belongs to. Carried alongside `program` from the point
  // it's known (persona-scoped program type generation) so consumers never need to re-derive it.
  userTypeId?: number;
  // The chosen published program version serving this slot (resolved server-side via join).
  activeVersion?: { token: string; name: string; version: number };
  // Display fields consumed by the Active Enrollment table, mapped from the API window below.
  startDate?: string;
  endDate?: string;
  // Raw window fields as returned by the enrollment-periods endpoint (camelCase JSON).
  startDateTime?: string | null;
  endDateTime?: string | null;
}

export interface SaveEnrollmentPeriod {
  academicYearId: number;
  enrollmentProgramToken: string;
  startDateTime?: string | null;
  endDateTime?: string | null;
}

export interface AcademicYearSummary {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
}

export interface EnrollmentApplicationProbe {
  isOpen: boolean;
  programType: ProgramTypeKey;
  enrollmentProgramToken?: string;
  academicYear: AcademicYearSummary;
  periodStartDateTime?: string | null;
  periodEndDateTime?: string | null;
}

/**
 * What the caller should do next for a program type, as decided server-side by combining their own
 * application state with the period's open/closed state. An application they already own outranks
 * period state: submitting in-window still reaches the dashboard after the window shuts.
 */
export type EnrollmentAccessDecision = 'Start' | 'Resume' | 'AlreadySubmitted' | 'Closed';

export interface EnrollmentApplicationStatus {
  decision: EnrollmentAccessDecision;
  isOpen: boolean;
  programType: ProgramTypeKey;
  enrollmentProgramToken?: string | null;
  sessionId?: string | null;
  currentPromptToken?: string | null;
  dateSubmitted?: string | null;
  academicYear?: AcademicYearSummary | null;
  periodStartDateTime?: string | null;
  periodEndDateTime?: string | null;
}

export interface EnrollmentApplicationResponse {
  enrollmentProgramToken: string;
  sessionId: string;
  status: string;
  nextPrompt: NextPrompt;
  academicYear: AcademicYearSummary;
}

/**
 * Aligned to GetEnrollmentSessionViewModel; used by sessionId-first bootstrap.
 *
 * Carries the program token but not the program tree. The tree is identical for every enrollee on the
 * program, so it comes from the cacheable definition endpoint (`getPromptDefinition`) rather than
 * being repeated in every session response.
 */
export interface GetEnrollmentSessionResponse {
  enrollmentProgramToken: string;
  sessionId: string;
  nextPrompt: NextPrompt;
  promptDataDictionary: PromptDataDictionary;
}
